import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import fr from 'facet-rest';
import FundingInstructionsCollapsibleCardContainer
  from 'Components/Modals/AccountFundingInstructions/FundingInstructionsCollapsibleCard/FundingInstructionsCollapsibleCardContainer';
import AccountCard from 'Components/ComponentLibrary/Organisms/Account/AccountCard';
import { wizardClasses } from 'Views/Page/Content/Templates/WizardTemplates/wizardCommonStyles';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import BaseOverride from 'Views/Page/Content/Templates/BaseOverride';
import FinancialInstitutionCard
  from 'Components/ComponentLibrary/Organisms/FinancialInstitutionCard/FinancialInstitutionCard';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import { FDIcon, FDPaper, FDStack } from '@facet-wealth/design';

const useStyles = buildUseStyles('AcatTransferFundsTemplateContent', (theme) => ({
  root: {
    ...wizardClasses.root(theme),
  },
  container: {
    ...wizardClasses.container(theme),
  },
  content: {
    ...wizardClasses.content(theme),
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  label: {
    marginBottom: theme.spacing(1),
  },
  arrow: {
    color: theme.palette.facet.mediumGray,
  },
  institution: {
    width: '100%',
    marginBottom: theme.spacing(3),
  },
  card: {
    width: '100%',
  },
  form: {
    padding: 0,
    marginTop: theme.spacing(2),
    '& > *': {
      marginBottom: theme.spacing(2),
    },
  },
  fundingInstructionsCard: {
    marginBottom: theme.spacing(3),
  },
}));

const AcatTransferFundsTemplateContent = ({
  className,
  errors,
  onChange,
  isPartialTransfer,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { pageContentResource: resource } = usePageContentContext();
  const isMobile = useMobileLayout();
  const fundingInstructionsRelationship = resource.relationship(fr.relationships.fundingInstructions.name)?.link('related');

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      <Box className={classes.container}>
        <Box className={classes.content}>
          <Title className={classes.title} text={resource.attr('title')} />
          <Subtitle text={resource.attr('subtitle')} />
          {fundingInstructionsRelationship && (
            <FundingInstructionsCollapsibleCardContainer
              className={classes.fundingInstructionsCard}
              title={'Schedule Transfer'}
              subtitle={`${isMobile ? 'Tap' : 'Click'} to view instructions`}
              fundingInstructionsLink={fundingInstructionsRelationship}
            />
          )}
          <Box className={classes.institution}>
            <Typography className={classes.label} color={'primary'}>
              {'From'}
            </Typography>
            <FinancialInstitutionCard
              className={classes.card}
              subText={resource.attr('institutionName')}
              logo={resource.attr('institutionLogoUrl')}
            />
          </Box>
          <ArrowDropDownIcon className={classes.arrow} fontSize={'large'} />
          <Box className={classes.institution}>
            <Typography className={classes.label} color={'primary'}>
              {'To'}
            </Typography>
            <AccountCard
              title={resource.attr('accountName')}
              image={resource.attr('facetLogoUrl')}
              subtitle={resource.attr('maskedAccountNumber')}
            />
          </Box>
        </Box>
        <MetaFormInline
          className={classes.form}
          noIndividualSubmitButtons
          resource={resource}
          metaOverrideSet={BaseOverride}
          errorsOverride={errors}
          FieldListProps={{
            ErrorListProps: {
              scrollIntoView: true,
            },
          }}
          onChange={(attr, value) => {
            return onChange(attr, value);
          }}
        />
        {isPartialTransfer && (
          <FDPaper color={'subtle'} padding={2}>
            <FDStack direction={'row'} spacing={1}>
              <FDIcon icon={'info'} color={'info'} />
              <RichTextDisplay content={resource.attr('partialTransferMessage')} />
            </FDStack>
          </FDPaper>
        )}
      </Box>
    </Box>
  );
};

AcatTransferFundsTemplateContent.defaultProps = {
  className: '',
  errors: undefined,
  onChange: () => {},
  isPartialTransfer: false,
};

AcatTransferFundsTemplateContent.propTypes = {
  className: PropTypes.string,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  isPartialTransfer: PropTypes.bool,
};

export default AcatTransferFundsTemplateContent;
